import { useEffect, useRef } from "react";
import { sendCatalogueViewerEvents } from "../../../components/catalogue/catalogueEngagementApi";
import {
  FLUSH_INTERVAL_MS,
  HEARTBEAT_INTERVAL_MS,
  createEventBuffer,
  heartbeatSeconds,
  openEventKey,
} from "./engagementQueue";

interface Options {
  catalogueId?: string;
  linkToken?: string;
  sessionToken?: string;
  /** The piece on screen (piece page). Heartbeats then count towards it. */
  productUniqueId?: string;
  /** public-status `engagementTracking`, and never for a studio preview. */
  enabled: boolean;
}

/**
 * Records the open, the piece and the time this viewer spends, under their viewer session. Time
 * is counted only while the tab is visible, sent every 30 s and on leaving (beacon). Nothing here
 * can block browsing: every send is fire-and-forget.
 */
export function useCatalogueEngagement({ catalogueId, linkToken, sessionToken, productUniqueId, enabled }: Options): void {
  const sentOpen = useRef("");
  const piece = useRef(productUniqueId);
  piece.current = productUniqueId;

  useEffect(() => {
    if (!enabled || !catalogueId || !linkToken || !sessionToken) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const buffer = createEventBuffer();
    const flush = (beacon: boolean) => {
      const events = buffer.drain();
      if (!events.length) return;
      sendCatalogueViewerEvents(catalogueId, { sessionToken, linkToken, events }, { beacon });
    };

    const key = openEventKey(sessionToken, productUniqueId);
    if (sentOpen.current !== key) {
      sentOpen.current = key;
      buffer.push(productUniqueId ? { type: "PRODUCT_VIEW", productUniqueId } : { type: "PAGE_VIEW" });
      flush(false);
    }

    let lastTick = Date.now();
    const visible = () => document.visibilityState === "visible";
    const beat = () => {
      const now = Date.now();
      if (!visible()) {
        lastTick = now;
        return;
      }
      const seconds = heartbeatSeconds(lastTick, now);
      lastTick = now;
      if (seconds > 0) {
        buffer.push({ type: "HEARTBEAT", seconds, ...(piece.current ? { productUniqueId: piece.current } : {}) });
      }
    };
    const onVisibility = () => {
      if (visible()) {
        lastTick = Date.now();
      } else {
        beat();
        flush(true);
      }
    };
    const onLeave = () => {
      beat();
      flush(true);
    };

    const beatTimer = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);
    const flushTimer = window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onLeave);

    return () => {
      window.clearInterval(beatTimer);
      window.clearInterval(flushTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onLeave);
      onLeave();
    };
    // productUniqueId changes restart the effect so the new piece gets its own open row.
  }, [enabled, catalogueId, linkToken, sessionToken, productUniqueId]);
}
